import { motion } from 'framer-motion';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import OpenAppBtn from '../../components/common/OpenAppBtn';
import { AmbientBackground } from '../../components/layout/AmbientBackground';
import { LoginGlassCard } from '../../components/login/LoginGlassCard';
import '../../components/login/login-page.css';
import GuestInquiryModal from '../../components/modals/GuestInquiryModal';
import SocialLoginButtons from '../../components/socialLogin/SocialLoginButtons';
import { ThirdPartyContext } from '../../contexts/ThirdPartyContext';
import SafariLoginInfoImage from '../../assets/login-info-image.svg';
import SimpleLoading from '../../components/SimpleLoading';
import { hasNaverAccessTokenHash } from '../../utils/naverCallback';

export default function LoginMainPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { state: collaborationState, setState: collaborationSetState } = useContext(ThirdPartyContext);
  const [guestInquiryisOpen, setGuestInquiryIsOpen] = useState(false);
  const [showSafariPopup, setShowSafariPopup] = useState(false);
  const [isNaverReturning, setIsNaverReturning] = useState(() => hasNaverAccessTokenHash());

  useEffect(() => {
    if (!searchParams.has('provNo')) {
      return;
    }

    collaborationSetState({
      ...collaborationState,
      thirdPartyId: searchParams.get('provNo') || '',
      thirdPartyCode: searchParams.get('provNo') === '11' ? 'VITAPORT' : undefined,
      thirdPartyUserId: searchParams.get('mberNo') || '',
      orderNumber: searchParams.get('orderNo') || '',
    });
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    const ua = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    const uaLower = ua.toLowerCase();

    const isIOS = /(iphone|ipad|ipod)/i.test(uaLower) || /iP(hone|ad|od)/.test(navigator.platform || '');
    const isInApp = /(kakaotalk|naver|fbios|fbav|instagram|line|daumapps|whale)/i.test(ua);
    const isMobileSafari =
      /safari/i.test(ua) && /apple/i.test(vendor) && !/(CriOS|FxiOS|EdgiOS|OPiOS|OPR|EdgA)/.test(ua);
    const isMacOSSafari = /Macintosh/.test(ua) && /Version\/\d+.*Safari/.test(ua) && /Apple/.test(vendor);

    const shouldShow = ((isIOS && isMobileSafari) || isMacOSSafari) && !isInApp;

    if (shouldShow) setShowSafariPopup(true);
  }, []);

  useEffect(() => {
    if (!isNaverReturning) return undefined;
    const onSettled = () => setIsNaverReturning(false);
    window.addEventListener('naver-redirect-settled', onSettled);
    const timeoutId = window.setTimeout(onSettled, 8000);
    return () => {
      window.removeEventListener('naver-redirect-settled', onSettled);
      window.clearTimeout(timeoutId);
    };
  }, [isNaverReturning]);

  return (
    <div className="soundpill-login-page bg-surface-container-lowest selection:bg-primary-container selection:text-on-primary-container">
      {isNaverReturning ? <SimpleLoading /> : null}
      <AmbientBackground variant="login" />

      <div className="soundpill-login-page__open-app">
        <OpenAppBtn variant="login" />
      </div>

      {showSafariPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="relative w-[320px] rounded-[16px] bg-[#1C1C1E] px-6 pb-5 pt-9 text-center text-white">
            <img src={SafariLoginInfoImage} alt={t('login.safari.imageAlt')} className="mx-auto mb-4 w-[150px]" />
            <p className="mb-2 text-[15px] font-medium leading-[22px]">{t('login.safari.eyebrow')}</p>
            <p className="mb-3 text-[24px] font-bold">
              {t('login.safari.titleLine1')}
              <br />
              {t('login.safari.titleLine2')}
            </p>
            <p className="mb-4 text-[19px] leading-[22px]">
              <span className="inline-block bg-[#1f3eef] p-[3px]">
                <span className="font-bold">{t('login.safari.highlight')}</span>
              </span>
              {t('login.safari.highlightAfter')}
              <br />
              {t('login.safari.body')}
            </p>
            <button
              type="button"
              className="w-full rounded-[8px] bg-[#4F68FC] p-4 text-[16px] font-semibold text-white"
              onClick={() => setShowSafariPopup(false)}
            >
              {t('login.safari.confirm')}
            </button>
          </div>
        </div>
      )}

      <motion.div
        className="soundpill-login-page__center"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <main className="soundpill-login-page__center-inner">
          <LoginGlassCard>
            <div className="soundpill-login-page__card-inner">
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="soundpill-login-page__title">SoundPill</h1>
                <p className="soundpill-login-page__welcome">{t('authDraft.welcome')}</p>
              </div>

              <SocialLoginButtons variant="draft" />

              <div className="text-center">
                <p className="soundpill-login-page__hint">{t('authDraft.loginHint')}</p>
                <button
                  type="button"
                  className="soundpill-login-page__problem"
                  onClick={() => setGuestInquiryIsOpen(true)}
                >
                  {t('login.problem')}
                </button>
              </div>
            </div>
          </LoginGlassCard>
        </main>
      </motion.div>

      <GuestInquiryModal isOpen={guestInquiryisOpen} setIsOpen={setGuestInquiryIsOpen} />
    </div>
  );
}
